import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

interface SKU {
  SkuId: string;
  ProductId: string;
  ProductSkuNum: number;
  Name: string;
  Description: string;
  CostMacroCategoryCd: string;
  CostMetricCd: string;
  CostMetricCost: number;
  CurrencyCd: string;
  CostIntervalCd: string;
  CostIntervalMin: number;
  CostIntervalMax: number;
  IsAutoProvisioned: boolean;
  IsReturnable: boolean;
  StatusCd: string;
  CreatedUser: string;
  UpdatedUser: string;
  CreatedDtm: string;
  UpdatedDtm: string;
  OrderQuantity: number;
  OrderInterval: number;
}

interface Order {
  OrderParentId: number;
  Description: string;
  OpportunityId: string;
  EngagementId: string;
  ServiceCodes: null;
  StatusCd: string;
  CreatedDtm: string;
  UpdatedDtm: string;
  CreatedUser: string;
  UpdatedUser: string;
  Requestor: null;
  Details: [
    {
      ProductName: string;
      AssetAdvice: string;
      SkuDetails: SKU[];
    }
  ];
}

interface ProductListState {
  isLoading: boolean;
  data: Order[] | null;
  error: string | null;
}

export interface ResponseError {
  message: string;
}

export interface RequestObject {
  Action: string;
  Status?: string;
  Search?: string;
}

const initialState: ProductListState = {
  isLoading: true,
  data: null,
  error: null,
};

export const fetchWorkspaceListData = createAsyncThunk<
  any,
  RequestObject,
  { rejectValue: ResponseError }
>("WorkspaceListSlice/fetchWorkspaceListData", async (requestObject, thunkApi) => {
  try {
    const response = await httpClient.post("/order", requestObject);

    if (response.status != 200 || response.data.Status !== "success") {
      if (response.data.Status == "error") {
        return thunkApi.rejectWithValue({
          message: response.data.Message,
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to fetch order list",
        });
      }
    }
    return response.data;
  } catch (error: any) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({
      message: "Failed to fetch order list",
    });
  }
});

const WorkspaceListSlice = createSlice({
  name: "WorkspaceListSlice",
  initialState,
  reducers: {
    resetWorkspaceListState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceListData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchWorkspaceListData.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        }
      )
      .addCase(
        fetchWorkspaceListData.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message || "Failed to fetch order list";
        }
      );
  },
});

export const { resetWorkspaceListState } = WorkspaceListSlice.actions;

export default WorkspaceListSlice.reducer;
